import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { FilterList as FilterListIcon } from '@material-ui/icons';
import OverdueTasksGrid from './components/OverdueTasksGrid.component';
import CoveragePeriodsGrid from './components/CoveragePeriodsGrid.component';
import ActiveFilesGrid from './components/ActiveFilesGrid.component';
import ClosedFilesGrid from './components/ClosedFilesGrid.component';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  filterContainer: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  filterRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  filterField: {
    minWidth: 250,
    flex: 1,
  },
  clearButton: {
    marginLeft: theme.spacing(2),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 'bold',
  },
  count: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
}));

const OpsManagerDashboardComponent = ({
  overdueTasks,
  coveragePeriods,
  activeFiles,
  closedFiles,
  teamMembers,
  clients,
  isLoadingDashboard,
  isLoadingTeamMembers,
  isLoadingClients,
}) => {
  const classes = useStyles();

  // Local filter state (client-side filtering)
  const [filters, setFilters] = useState({
    selectedUserIds: [],
    clientId: null,
    isVIP: null,
  });

  // Client-side filtering using useMemo
  const filteredOverdueTasks = useMemo(() => {
    let result = [...overdueTasks];

    if (filters.selectedUserIds.length > 0) {
      result = result.filter(task =>
        filters.selectedUserIds.includes(task.rcUserId)
      );
    }
    if (filters.clientId) {
      const selectedClient = clients.find(c => c.clientRecordId === filters.clientId);
      if (selectedClient) {
        const clientName = selectedClient.shortName || selectedClient.legalName;
        result = result.filter(task => task.clientName === clientName);
      }
    }
    if (filters.isVIP === true) {
      result = result.filter(task => task.isVIP === true);
    }

    return result;
  }, [overdueTasks, filters, clients]);

  const filteredCoveragePeriods = useMemo(() => {
    let result = [...coveragePeriods];

    if (filters.selectedUserIds.length > 0) {
      result = result.filter(period =>
        filters.selectedUserIds.includes(period.rcUserId)
      );
    }
    // Note: Coverage periods don't have client or VIP information

    return result;
  }, [coveragePeriods, filters]);

  const filteredActiveFiles = useMemo(() => {
    let result = [...activeFiles];

    if (filters.selectedUserIds.length > 0) {
      result = result.filter(file =>
        filters.selectedUserIds.includes(file.rcUserId)
      );
    }
    if (filters.clientId) {
      const selectedClient = clients.find(c => c.clientRecordId === filters.clientId);
      if (selectedClient) {
        const clientName = selectedClient.shortName || selectedClient.legalName;
        result = result.filter(file => file.clientName === clientName);
      }
    }
    if (filters.isVIP === true) {
      result = result.filter(file => file.isVIP === true);
    }

    return result;
  }, [activeFiles, filters, clients]);

  const filteredClosedFiles = useMemo(() => {
    let result = [...closedFiles];

    if (filters.selectedUserIds.length > 0) {
      result = result.filter(file =>
        filters.selectedUserIds.includes(file.rcUserId)
      );
    }
    if (filters.clientId) {
      const selectedClient = clients.find(c => c.clientRecordId === filters.clientId);
      if (selectedClient) {
        const clientName = selectedClient.shortName || selectedClient.legalName;
        result = result.filter(file => file.clientName === clientName);
      }
    }
    if (filters.isVIP === true) {
      result = result.filter(file => file.isVIP === true);
    }

    return result;
  }, [closedFiles, filters, clients]);

  // Filter change handlers
  const selectedUsers = teamMembers.filter(user =>
    filters.selectedUserIds.includes(user.internalUserId)
  );

  const selectedClient = clients.find(
    client => client.clientRecordId === filters.clientId
  ) || null;

  const handleRcChange = (event, newValue) => {
    setFilters(prev => ({
      ...prev,
      selectedUserIds: newValue.map(user => user.internalUserId),
    }));
  };

  const handleClientChange = (event, newValue) => {
    setFilters(prev => ({
      ...prev,
      clientId: newValue?.clientRecordId || null,
    }));
  };

  const handleVipChange = (event) => {
    setFilters(prev => ({
      ...prev,
      isVIP: event.target.checked ? true : null,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      selectedUserIds: [],
      clientId: null,
      isVIP: null,
    });
  };

  const handleFileClick = (fileId) => {
    window.open(`/authorizations/${fileId}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Container maxWidth="xl" className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h4">Operations Manager Dashboard</Typography>
      </Box>

      {/* Filters */}
      <Paper className={classes.filterContainer}>
        <Box className={classes.filterRow}>
          <Autocomplete
            multiple
            className={classes.filterField}
            options={teamMembers}
            value={selectedUsers}
            onChange={handleRcChange}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            getOptionSelected={(option, value) =>
              option.internalUserId === value.internalUserId
            }
            loading={isLoadingTeamMembers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Relocation Coordinators"
                placeholder={selectedUsers.length === 0 ? 'All' : ''}
                variant="outlined"
                size="small"
              />
            )}
          />

          <Autocomplete
            className={classes.filterField}
            options={clients}
            value={selectedClient}
            onChange={handleClientChange}
            getOptionLabel={(option) => option.shortName || option.legalName}
            getOptionSelected={(option, value) =>
              option.clientRecordId === value.clientRecordId
            }
            loading={isLoadingClients}
            renderInput={(params) => (
              <TextField {...params} label="Client" variant="outlined" size="small" />
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={filters.isVIP === true}
                onChange={handleVipChange}
                color="primary"
              />
            }
            label="VIP Only"
          />

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={handleClearFilters}
            className={classes.clearButton}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Overdue Tasks Section */}
      <Paper className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="h6" className={classes.title}>
            Overdue Tasks
            <span className={classes.count}>({filteredOverdueTasks.length})</span>
          </Typography>
        </Box>
        <OverdueTasksGrid
          tasks={filteredOverdueTasks}
          isLoading={isLoadingDashboard}
          onFileClick={handleFileClick}
        />
      </Paper>

      {/* Coverage Periods Section */}
      <Paper className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="h6" className={classes.title}>
            Coverage Periods
            <span className={classes.count}>({filteredCoveragePeriods.length})</span>
          </Typography>
        </Box>
        <CoveragePeriodsGrid
          periods={filteredCoveragePeriods}
          isLoading={isLoadingDashboard}
        />
      </Paper>

      {/* Active Files Section */}
      <Paper className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="h6" className={classes.title}>
            Active Files
            <span className={classes.count}>({filteredActiveFiles.length})</span>
          </Typography>
        </Box>
        <ActiveFilesGrid
          files={filteredActiveFiles}
          isLoading={isLoadingDashboard}
          onFileClick={handleFileClick}
        />
      </Paper>

      {/* Closed Files Section */}
      <Paper className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography variant="h6" className={classes.title}>
            Recently Closed Files - Last 30 Days
            <span className={classes.count}>({filteredClosedFiles.length})</span>
          </Typography>
        </Box>
        <ClosedFilesGrid
          files={filteredClosedFiles}
          isLoading={isLoadingDashboard}
          onFileClick={handleFileClick}
        />
      </Paper>
    </Container>
  );
};

OpsManagerDashboardComponent.propTypes = {
  // Data
  overdueTasks: PropTypes.arrayOf(
    PropTypes.shape({
      taskId: PropTypes.number.isRequired,
      rcName: PropTypes.string.isRequired,
      rcUserId: PropTypes.number.isRequired,
      taskTitle: PropTypes.string.isRequired,
      fileNumber: PropTypes.string.isRequired,
      fileId: PropTypes.number.isRequired,
      clientName: PropTypes.string.isRequired,
      employeeName: PropTypes.string,
      dueDate: PropTypes.string,
      daysOverdue: PropTypes.number,
      taskTypeName: PropTypes.string,
      isVIP: PropTypes.bool,
    })
  ).isRequired,
  coveragePeriods: PropTypes.arrayOf(
    PropTypes.shape({
      coverageId: PropTypes.number.isRequired,
      rcName: PropTypes.string.isRequired,
      rcUserId: PropTypes.number.isRequired,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      fileCount: PropTypes.number,
      coveringUserName: PropTypes.string,
      coveringRole: PropTypes.string,
    })
  ).isRequired,
  activeFiles: PropTypes.arrayOf(
    PropTypes.shape({
      fileId: PropTypes.number.isRequired,
      rcName: PropTypes.string.isRequired,
      rcUserId: PropTypes.number.isRequired,
      fileNumber: PropTypes.string.isRequired,
      clientName: PropTypes.string.isRequired,
      initDate: PropTypes.string,
      isVIP: PropTypes.bool,
      fileType: PropTypes.string,
      employeeName: PropTypes.string,
      relocationPolicy: PropTypes.string,
    })
  ).isRequired,
  closedFiles: PropTypes.arrayOf(
    PropTypes.shape({
      fileId: PropTypes.number.isRequired,
      rcName: PropTypes.string.isRequired,
      rcUserId: PropTypes.number.isRequired,
      fileNumber: PropTypes.string.isRequired,
      clientName: PropTypes.string.isRequired,
      initDate: PropTypes.string,
      isVIP: PropTypes.bool,
      fileType: PropTypes.string,
      employeeName: PropTypes.string,
      closedDate: PropTypes.string,
      statusDescription: PropTypes.string,
    })
  ).isRequired,
  teamMembers: PropTypes.arrayOf(
    PropTypes.shape({
      internalUserId: PropTypes.number.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      email: PropTypes.string,
    })
  ).isRequired,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      clientRecordId: PropTypes.number.isRequired,
      shortName: PropTypes.string,
      legalName: PropTypes.string,
    })
  ).isRequired,

  // Loading states
  isLoadingDashboard: PropTypes.bool.isRequired,
  isLoadingTeamMembers: PropTypes.bool.isRequired,
  isLoadingClients: PropTypes.bool.isRequired,

};

OpsManagerDashboardComponent.defaultProps = {
  overdueTasks: [],
  coveragePeriods: [],
  activeFiles: [],
  closedFiles: [],
  teamMembers: [],
  clients: [],
  isLoadingDashboard: false,
  isLoadingTeamMembers: false,
  isLoadingClients: false,
};

export default OpsManagerDashboardComponent;
